// ─────────────────────────────────────────────────────────────────────────────
// useFlowTour.js — Self-contained tour hook for the Workflow Studio
//
// Checks whether the current user has already seen the flow builder tour.
// If not, auto-starts it after a short delay (so the page has fully mounted).
//
// Tour key: 'flow_builder_v1'
// State persistence: localStorage (instant) + server API (durable)
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api';
import { getFlowBuilderSteps } from './tracks/flowBuilderTour';

const TOUR_KEY = 'flow_builder_v1';
const LOCAL_CACHE_PREFIX = 'aidas_tour_state_';

export const useFlowTour = (userId, token) => {
  const [run, setRun] = useState(false);
  const [tourServerState, setTourServerState] = useState({});
  const hasChecked = useRef(false);

  // Memoize steps once — no need to re-create on every render
  const [steps] = useState(() => getFlowBuilderSteps());

  // ── Check tour state on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!userId || hasChecked.current) return;
    hasChecked.current = true;

    const localKey = `${LOCAL_CACHE_PREFIX}${userId}`;

    // Fast path: check localStorage first to avoid API round-trip on re-visits
    try {
      const cached = localStorage.getItem(localKey);
      if (cached) {
        const state = JSON.parse(cached);
        if (state[TOUR_KEY]) return; // Already seen — do nothing
      }
    } catch (e) {
      // Corrupted cache — fall through to API
    }

    // Slow path: fetch from server
    const fetchState = async () => {
      try {
        const serverState = await apiFetch('/users/me/tour-state', token);
        const state = serverState || {};
        setTourServerState(state);

        // Write to localStorage so subsequent visits skip the API call
        try {
          localStorage.setItem(localKey, JSON.stringify(state));
        } catch (e) {}

        if (!state[TOUR_KEY]) {
          // Delay ensures the page has fully rendered before the tour starts
          setTimeout(() => setRun(true), 1400);
        }
      } catch (e) {
        // If API fails and there's no local cache, still show the tour
        // (better to show it once too many than never show it at all)
        try {
          const cached = localStorage.getItem(localKey);
          if (!cached) setTimeout(() => setRun(true), 1400);
        } catch {}
      }
    };

    fetchState();
  }, [userId, token]);

  // ── Mark tour as seen (called by TourEngine on finish or skip) ───────────
  const markSeen = useCallback(async () => {
    setRun(false);
    if (!userId) return;

    const localKey = `${LOCAL_CACHE_PREFIX}${userId}`;
    const newState = { ...tourServerState, [TOUR_KEY]: true };
    setTourServerState(newState);

    // Optimistic localStorage update — instant
    try {
      localStorage.setItem(localKey, JSON.stringify(newState));
    } catch (e) {}

    // Persist to server
    try {
      await apiFetch('/users/me/tour-state', token, {
        method: 'POST',
        body: JSON.stringify({ [TOUR_KEY]: true }),
      });
    } catch (e) {
      console.error('[useFlowTour] Failed to persist tour state to server:', e);
    }
  }, [tourServerState, userId, token]);

  // ── Allow restarting the tour (e.g. from profile menu) ──────────────────
  const restartTour = useCallback(() => {
    setRun(true);
  }, []);

  return { steps, run, setRun, markSeen, restartTour };
};
